// On-device storage for assessment forms.
//
// Every edit a therapist makes is written here before anything is sent to the
// server, so closing the tab, losing signal or the token expiring cannot lose
// an answer.
//
// Encryption ("the vault")
// ------------------------
// Each user on each device has a random AES-GCM data key. Forms and the offline
// student list are encrypted under it. The data key itself is only ever stored
// wrapped (encrypted), twice:
//
//   serverWrapped  under a per-user secret from GET /assessments/device-key.
//                  The secret is fetched after an online sign-in and kept in
//                  memory only, so it is never on the device.
//   pinWrapped     under a key derived from the therapist's offline PIN
//                  (PBKDF2-SHA256). This is what unlocks the data offline.
//
// Online, the server copy unlocks the data with no prompt. Offline, the PIN
// does. Five wrong PINs delete the PIN copy outright -- after that no PIN can
// unlock this device until an online sign-in recovers the data key through the
// server copy and a new PIN is set. Forgetting a PIN therefore never loses
// unsynced answers.
//
// Limits, stated plainly: the attempt counter lives in browser storage, so the
// 5-attempt limit stops a person at the keyboard, not someone who has copied
// the browser's files and guesses offline -- for them the PIN-derived key cost
// (PBKDF2, many iterations) is the barrier, which is why longer PINs are
// allowed. And while the vault is unlocked, anyone using the device can see the
// forms; that is the device screen lock's job.
//
// Stores
//   vault     { userId, salt, iterations, serverWrapped, pinWrapped, failedAttempts,
//               lockedOut, declinedAt, offlineEnabled, pendingDeviceEvent,
//               lastSignInAt, profile }
//   forms     { uuid, userId, updatedAt, pendingCount, kv, iv, data }   (data encrypted)
//   students  { userId, iv, data }                                     (encrypted)
//   cache     { id, value }                     (non-sensitive, e.g. form definitions)
//   keys      Phase 2 per-user keys -- read once to migrate, then deleted

const DB_NAME = 'wmf-assessments';
const DB_VERSION = 2;
const KEY_VERSION = 2;

export const MAX_PIN_ATTEMPTS = 5;
export const OFFLINE_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
export const PIN_PATTERN = /^\d{6,12}$/;
const PBKDF2_ITERATIONS = 600000;

let dbPromise = null;

function openDb() {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined' || !window.crypto?.subtle) {
        reject(new Error('This browser cannot store drafts on the device'));
        return;
      }
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains('keys')) db.createObjectStore('keys', { keyPath: 'userId' });
        if (!db.objectStoreNames.contains('forms')) {
          const forms = db.createObjectStore('forms', { keyPath: 'uuid' });
          forms.createIndex('userId', 'userId');
        }
        if (!db.objectStoreNames.contains('cache')) db.createObjectStore('cache', { keyPath: 'id' });
        if (!db.objectStoreNames.contains('vault')) db.createObjectStore('vault', { keyPath: 'userId' });
        if (!db.objectStoreNames.contains('students')) db.createObjectStore('students', { keyPath: 'userId' });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
      req.onblocked = () => reject(new Error('Device storage is blocked by another tab'));
    }).catch((err) => {
      dbPromise = null;
      throw err;
    });
  }
  return dbPromise;
}

function tx(storeNames, mode, fn) {
  return openDb().then((db) => new Promise((resolve, reject) => {
    const t = db.transaction(storeNames, mode);
    let result;
    Promise.resolve(fn(t)).then((r) => { result = r; }, reject);
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error || new Error('Device storage transaction aborted'));
  }));
}

function request(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

const getRow = (store, key) => tx([store], 'readonly', (t) => request(t.objectStore(store).get(key)));
const putRow = (store, row) => tx([store], 'readwrite', (t) => { t.objectStore(store).put(row); });

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export class VaultError extends Error {
  // code: locked | no-pin | wrong-pin | locked-out | expired | damaged
  constructor(code, message, extra = {}) {
    super(message);
    this.code = code;
    Object.assign(this, extra);
  }
}

// ---------------------------------------------------------------------------
// Key handling
// ---------------------------------------------------------------------------

const subtle = () => window.crypto.subtle;
const unlocked = new Map();   // userId -> data key (memory only)

const vaultAad = (userId) => new TextEncoder().encode(`wmf-vault:${userId}`);

function b64ToBytes(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

async function wrap(dataKey, wrappingKey, userId) {
  const raw = await subtle().exportKey('raw', dataKey);
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const data = await subtle().encrypt({ name: 'AES-GCM', iv, additionalData: vaultAad(userId) }, wrappingKey, raw);
  return { iv, data };
}

async function unwrap(blob, wrappingKey, userId) {
  const raw = await subtle().decrypt({ name: 'AES-GCM', iv: blob.iv, additionalData: vaultAad(userId) },
    wrappingKey, blob.data);
  // Extractable so it can be re-wrapped under a new PIN; it only ever lives in memory.
  return subtle().importKey('raw', raw, { name: 'AES-GCM' }, true, ['encrypt', 'decrypt']);
}

function importServerKey(b64) {
  return subtle().importKey('raw', b64ToBytes(b64), { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

async function pinKey(pin, salt, iterations) {
  const base = await subtle().importKey('raw', new TextEncoder().encode(pin), 'PBKDF2', false, ['deriveKey']);
  return subtle().deriveKey({ name: 'PBKDF2', salt, iterations, hash: 'SHA-256' }, base,
    { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}

function dataKeyFor(userId) {
  const key = unlocked.get(String(userId));
  if (!key) throw new VaultError('locked', 'Saved assessments on this device are locked');
  return key;
}

async function encrypt(userId, value) {
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const data = await subtle().encrypt({ name: 'AES-GCM', iv }, dataKeyFor(userId),
    new TextEncoder().encode(JSON.stringify(value)));
  return { iv, data };
}

async function decrypt(userId, iv, data) {
  const plain = await subtle().decrypt({ name: 'AES-GCM', iv }, dataKeyFor(userId), data);
  return JSON.parse(new TextDecoder().decode(plain));
}

// Phase 2 stored forms under a non-extractable per-user key. Re-encrypt them
// under the data key, then drop the old key.
async function migrateLegacy(userId) {
  const legacy = await getRow('keys', userId);
  if (!legacy?.key) return;
  const rows = await tx(['forms'], 'readonly', (t) => request(t.objectStore('forms').index('userId').getAll(userId)));
  for (const row of rows) {
    if (row.kv === KEY_VERSION) continue;
    try {
      const plain = await subtle().decrypt({ name: 'AES-GCM', iv: row.iv }, legacy.key, row.data);
      const { iv, data } = await encrypt(userId, JSON.parse(new TextDecoder().decode(plain)));
      await putRow('forms', { ...row, kv: KEY_VERSION, iv, data });
    } catch (err) {
      console.error('Could not migrate a locally stored assessment', row.uuid, err);
      return; // keep the legacy key so nothing becomes unreadable
    }
  }
  await tx(['keys'], 'readwrite', (t) => { t.objectStore('keys').delete(userId); });
}

// ---------------------------------------------------------------------------
// Vault API
// ---------------------------------------------------------------------------

export function isUnlocked(userId) {
  return unlocked.has(String(userId));
}

export function lockVault(userId) {
  if (userId === undefined) unlocked.clear();
  else unlocked.delete(String(userId));
}

function describe(v) {
  return v ? {
    userId: v.userId,
    profile: v.profile,
    hasPin: !!v.pinWrapped,
    lockedOut: !!v.lockedOut,
    declined: !!v.declinedAt,
    offlineEnabled: v.offlineEnabled !== false,
    pendingDeviceEvent: v.pendingDeviceEvent || null,
    attemptsLeft: MAX_PIN_ATTEMPTS - (v.failedAttempts || 0),
    lastSignInAt: v.lastSignInAt,
    withinWindow: !!v.lastSignInAt && Date.now() - v.lastSignInAt <= OFFLINE_WINDOW_MS,
  } : null;
}

export async function vaultStatus(userId) {
  return describe(await getRow('vault', String(userId)));
}

// Users who could unlock offline on this device, most recent first.
export async function offlineProfiles() {
  const rows = await tx(['vault'], 'readonly', (t) => request(t.objectStore('vault').getAll()));
  return rows.map(describe).sort((a, b) => (b.lastSignInAt || 0) - (a.lastSignInAt || 0));
}

// Called after every online sign-in. Creates the vault on first use.
// Returns the vault status, whose hasPin / lockedOut say whether a PIN must
// be chosen before the portal opens.
export async function unlockWithServerKey(userId, serverKeyB64, profile, offlineEnabled = true) {
  const id = String(userId);
  const serverKey = await importServerKey(serverKeyB64);
  let v = await getRow('vault', id);
  let dataKey;
  if (v) {
    try {
      dataKey = await unwrap(v.serverWrapped, serverKey, id);
    } catch {
      throw new VaultError('damaged', 'Assessments saved on this device cannot be unlocked by your account');
    }
  } else {
    dataKey = await subtle().generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
    v = {
      userId: id,
      salt: window.crypto.getRandomValues(new Uint8Array(16)),
      iterations: PBKDF2_ITERATIONS,
      serverWrapped: await wrap(dataKey, serverKey, id),
      pinWrapped: null,
      failedAttempts: 0,
      lockedOut: false,
    };
  }
  v = { ...v, lastSignInAt: Date.now(), profile, offlineEnabled };
  await putRow('vault', v);
  unlocked.set(id, dataKey);
  await migrateLegacy(id);
  return describe(v);
}

export async function setPin(userId, pin) {
  const id = String(userId);
  if (!PIN_PATTERN.test(pin)) throw new VaultError('invalid', 'PIN must be 6 to 12 digits');
  const dataKey = dataKeyFor(id);
  const v = await getRow('vault', id);
  const salt = window.crypto.getRandomValues(new Uint8Array(16));
  const pinWrapped = await wrap(dataKey, await pinKey(pin, salt, PBKDF2_ITERATIONS), id);
  await putRow('vault', {
    ...v, salt, iterations: PBKDF2_ITERATIONS, pinWrapped, failedAttempts: 0, lockedOut: false,
    declinedAt: null, pinSetAt: Date.now(), pendingDeviceEvent: { event: 'pin_set', at: Date.now() },
  });
}

// "Skip -- I won't work offline on this device". Remembered for this user on
// this device so the PIN prompt does not come back at every sign-in.
export async function declineOffline(userId) {
  const v = await getRow('vault', String(userId));
  // Declining also clears a lockout: there is no PIN to recover any more.
  if (v) {
    await putRow('vault', { ...v, declinedAt: Date.now(), lockedOut: false, failedAttempts: 0,
      pendingDeviceEvent: { event: 'pin_skipped', at: Date.now() } });
  }
}

// Offline access switched off for this user (or by them): the PIN can no
// longer unlock anything on this device. The server copy still works online.
export async function removePin(userId) {
  const v = await getRow('vault', String(userId));
  if (v) {
    await putRow('vault', { ...v, pinWrapped: null, failedAttempts: 0, lockedOut: false,
      pendingDeviceEvent: { event: 'pin_removed', at: Date.now() } });
  }
}

// The last PIN change on this device waits here until the server has it, so
// a report cut off by a closed tab or lost signal is sent at the next sign-in.
export async function clearPendingDeviceEvent(userId, sent) {
  const v = await getRow('vault', String(userId));
  if (v?.pendingDeviceEvent && v.pendingDeviceEvent.at === sent.at && v.pendingDeviceEvent.event === sent.event) {
    await putRow('vault', { ...v, pendingDeviceEvent: null });
  }
}

export async function unlockWithPin(userId, pin) {
  const id = String(userId);
  const v = await getRow('vault', id);
  if (!v || (!v.pinWrapped && !v.lockedOut)) {
    throw new VaultError('no-pin', 'Offline access has not been set up on this device');
  }
  if (v.lockedOut) {
    throw new VaultError('locked-out', 'Too many incorrect PINs. Sign in online to unlock.');
  }
  if (v.offlineEnabled === false) {
    throw new VaultError('disabled', 'Offline access is not enabled for your account.');
  }
  if (!v.lastSignInAt || Date.now() - v.lastSignInAt > OFFLINE_WINDOW_MS) {
    throw new VaultError('expired', 'Offline access expires 7 days after your last online sign-in. Sign in online to continue.');
  }
  let dataKey;
  try {
    dataKey = await unwrap(v.pinWrapped, await pinKey(pin, v.salt, v.iterations), id);
  } catch {
    const failedAttempts = (v.failedAttempts || 0) + 1;
    if (failedAttempts >= MAX_PIN_ATTEMPTS) {
      // Destroy the PIN copy: no further guess can succeed on this device.
      await putRow('vault', { ...v, pinWrapped: null, failedAttempts, lockedOut: true,
        pendingDeviceEvent: { event: 'pin_locked_out', at: Date.now() } });
      throw new VaultError('locked-out', 'Too many incorrect PINs. Sign in online to unlock.');
    }
    await putRow('vault', { ...v, failedAttempts });
    throw new VaultError('wrong-pin', 'Incorrect PIN', { attemptsLeft: MAX_PIN_ATTEMPTS - failedAttempts });
  }
  await putRow('vault', { ...v, failedAttempts: 0 });
  unlocked.set(id, dataKey);
  await migrateLegacy(id);
  return describe(v);
}

// ---------------------------------------------------------------------------
// Merging two copies of a record
// ---------------------------------------------------------------------------

// Two tabs (or a tab and a background sync) can each hold a copy of the same
// form. Saving one must never drop an unsynced edit that only the other has,
// so pending edits are merged per field, newest wins, and the server snapshot
// with the higher revision is kept.
export function mergeRecords(stored, incoming) {
  if (!stored) return incoming;
  const pending = { ...(stored.pending || {}) };
  Object.entries(incoming.pending || {}).forEach(([k, e]) => {
    if (!pending[k] || e.t >= pending[k].t) pending[k] = e;
  });
  // A key the incoming copy just synced (removed from its pending) must not be
  // resurrected from an older stored copy carrying the same edit.
  (incoming.syncedKeys || []).forEach(({ key, t }) => {
    if (pending[key] && pending[key].t <= t) delete pending[key];
  });
  const server = (stored.server?.revision ?? -1) > (incoming.server?.revision ?? -1)
    ? stored.server : incoming.server;
  return { ...stored, ...incoming, server, pending, syncedKeys: undefined };
}

// ---------------------------------------------------------------------------
// Forms, students, cache
// ---------------------------------------------------------------------------

export async function isAvailable() {
  try {
    await openDb();
    return true;
  } catch {
    return false;
  }
}

// Ask the browser not to evict this origin's storage under pressure. Best
// effort: browsers may decline, and Safari ignores it for non-installed sites.
export async function requestPersistence() {
  try {
    if (navigator.storage?.persist && !(await navigator.storage.persisted())) {
      await navigator.storage.persist();
    }
  } catch { /* best effort */ }
}

export async function loadForm(userId, uuid) {
  const row = await getRow('forms', uuid);
  if (!row || String(row.userId) !== String(userId)) return null;
  return decrypt(userId, row.iv, row.data);
}

export async function listForms(userId) {
  const rows = await tx(['forms'], 'readonly',
    (t) => request(t.objectStore('forms').index('userId').getAll(String(userId))));
  const out = [];
  for (const row of rows) {
    try {
      out.push(await decrypt(userId, row.iv, row.data));
    } catch (err) {
      if (err instanceof VaultError) throw err;
      // Never delete what cannot be read; just leave it out of the list.
      console.error('Could not read a locally stored assessment', row.uuid, err);
    }
  }
  return out;
}

// Writes are serialised per form so a slow write can never land after, and
// overwrite, a newer one.
const writeChains = new Map();

export function saveForm(userId, record) {
  const uuid = record.uuid;
  const prev = writeChains.get(uuid) || Promise.resolve();
  const next = prev.catch(() => {}).then(async () => {
    const storedRow = await getRow('forms', uuid);
    let stored = null;
    if (storedRow && String(storedRow.userId) === String(userId)) {
      try { stored = await decrypt(userId, storedRow.iv, storedRow.data); } catch { stored = null; }
    }
    const merged = mergeRecords(stored, { ...record, userId: String(userId), updatedAt: Date.now() });
    const { iv, data } = await encrypt(userId, merged);
    await putRow('forms', {
      uuid,
      userId: String(userId),
      updatedAt: merged.updatedAt,
      pendingCount: Object.keys(merged.pending || {}).length,
      kv: KEY_VERSION,
      iv,
      data,
    });
    return merged;
  });
  writeChains.set(uuid, next);
  next.finally(() => { if (writeChains.get(uuid) === next) writeChains.delete(uuid); });
  return next;
}

export async function deleteForm(uuid) {
  await (writeChains.get(uuid) || Promise.resolve()).catch(() => {});
  return tx(['forms'], 'readwrite', (t) => { t.objectStore('forms').delete(uuid); });
}

export async function putStudents(userId, students) {
  const { iv, data } = await encrypt(userId, { students, savedAt: Date.now() });
  await putRow('students', { userId: String(userId), iv, data });
}

export async function getStudents(userId) {
  const row = await getRow('students', String(userId));
  if (!row) return null;
  return decrypt(userId, row.iv, row.data);
}

export async function deleteStudents(userId) {
  await tx(['students'], 'readwrite', (t) => { t.objectStore('students').delete(String(userId)); });
}

// A random id for this browser, so managers can tell a user's devices apart.
export async function getDeviceId() {
  let id = await getCache('device:id');
  if (!id) {
    if (window.crypto.randomUUID) {
      id = window.crypto.randomUUID();
    } else {
      const b = window.crypto.getRandomValues(new Uint8Array(16));
      b[6] = (b[6] & 0x0f) | 0x40;
      b[8] = (b[8] & 0x3f) | 0x80;
      const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
      id = `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
    }
    await putCache('device:id', id);
  }
  return id;
}

export async function getCache(id) {
  const row = await getRow('cache', id);
  return row ? row.value : null;
}

export function putCache(id, value) {
  return putRow('cache', { id, value });
}
